import React from "react";
import { CCCheckbox, CCField } from "@pymblecitizencoders/cc-ui";
import { EventDoc } from "../../types/event";
import { useEventTransport } from "../../hooks/useEventTransport";
import {
	NO_TRANSPORT_CODE,
	buildTransportValue,
	defaultAckText,
	type TransportChange,
} from "../../utils/transport";
import "./eventManager.css";

interface EventTransportTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/** Transport tab: how the group travels, notes, and an optional carer acknowledgement. */
export const EventTransportTab: React.FC<EventTransportTabProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const { options } = useEventTransport();
	const transport = event.Transport;
	const code = transport?.Code || NO_TRANSPORT_CODE;
	const required = transport?.AcknowledgementRequired === true;
	const methodLabel = options.find((o) => o.id === code)?.label ?? code;

	const update = (change: TransportChange) =>
		onChange({ Transport: buildTransportValue(transport, change) });

	return (
		<div className="em-transport">
			<div className="em-transport-options" role="radiogroup" aria-label="Transport">
				{options.map((option) => (
					<button
						type="button"
						key={option.id}
						role="radio"
						aria-checked={code === option.id}
						className={
							"em-transport-option" +
							(code === option.id ? " em-transport-option--selected" : "")
						}
						title={option.description}
						disabled={readOnly}
						onClick={() => update({ code: option.id })}
					>
						{option.imageUrl && <img src={option.imageUrl} alt="" />}
						<span>{option.label}</span>
					</button>
				))}
			</div>
			<div className="em-transport-fields">
				<CCField
					label="Transport notes"
					multiline
					rows={4}
					fullWidth
					maxLength={500}
					value={transport?.Notes ?? ""}
					disabled={readOnly}
					onChange={(e) => update({ notes: e.target.value })}
				/>
				{code !== NO_TRANSPORT_CODE && (
					<>
						<CCCheckbox
							id="em-transport-ack"
							label="Acknowledgement required"
							checked={required}
							disabled={readOnly}
							onChange={(e) =>
								update({ acknowledgementRequired: e.target.checked })
							}
						/>
						{required && (
							<CCField
								label="Acknowledgement text"
								fullWidth
								maxLength={1000}
								placeholder={defaultAckText(methodLabel)}
								title={defaultAckText(methodLabel)}
								value={transport?.AcknowledgementText ?? ""}
								disabled={readOnly}
								onChange={(e) =>
									update({ acknowledgementText: e.target.value })
								}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};
